import { useEffect, useState } from 'react';
import { HelpTooltip } from '../components/HelpTooltip/HelpTooltip';
import type { Class, ProjectKind, Sample, TrainedModel } from '../data/types';
import { LossCurve } from './LossCurve';
import { TrainingTip } from './TrainingTip';
import { checkAudioTrainability } from '../ml/audioTrainer';
import {
  DEFAULT_TRAIN_HYPERPARAMS,
  TRAIN_HYPERPARAM_BOUNDS,
  checkTrainability,
  type TrainHyperparams,
} from '../ml/trainer';
import type { TrainingState } from '../stores/useProjectsStore';
import './TrainPanel.css';

export interface TrainPanelProps {
  classes: Class[];
  samples: Sample[];
  /** Project kind drives which trainability minimum to enforce. Defaults to image. */
  kind?: ProjectKind;
  trained: TrainedModel | null;
  training: TrainingState;
  onTrain: (hyperparams?: TrainHyperparams) => void;
  onCancel: () => void;
  /** Callback for the "Test the model" button — visible only when
   *  trained. The test panel hosts the "now do something with it"
   *  actions (download / publish / make it do something) so this
   *  card can stay focused on training. */
  onTest?: () => void;
  /** Callback for the "Can you fool it?" button — visible only when
   *  trained. Opens the held-out evaluation panel ("can you fool it"
   *  game per PLAN.md). Image-only today; the parent component is
   *  responsible for not passing this for audio / pose projects until
   *  the audio + pose evaluators ship. */
  onQuiz?: () => void;
}

export function TrainPanel({
  classes,
  samples,
  kind = 'image',
  trained,
  training,
  onTrain,
  onCancel,
  onTest,
  onQuiz,
}: TrainPanelProps): JSX.Element {
  const trainability =
    kind === 'audio'
      ? checkAudioTrainability(classes, samples)
      : checkTrainability(classes, samples);
  const inProgress =
    training.status === 'loading-mobilenet' ||
    training.status === 'extracting' ||
    training.status === 'training';
  // Bumps on every false → true edge so the tip ticker reshuffles per
  // training run instead of resuming mid-cycle from the last one.
  const [runId, setRunId] = useState(0);
  useEffect(() => {
    if (inProgress) setRunId((k) => k + 1);
  }, [inProgress]);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [hyperparams, setHyperparams] =
    useState<Required<TrainHyperparams>>(DEFAULT_TRAIN_HYPERPARAMS);
  const isCustomHyperparams =
    hyperparams.epochs !== DEFAULT_TRAIN_HYPERPARAMS.epochs ||
    hyperparams.batchSize !== DEFAULT_TRAIN_HYPERPARAMS.batchSize ||
    hyperparams.learningRate !== DEFAULT_TRAIN_HYPERPARAMS.learningRate;

  const trainedStaleness = trained ? sampleCountsAreStale(trained, classes, samples) : false;

  return (
    <section className="bl-train" aria-label="Training">
      <header className="bl-train__header">
        <h2 className="bl-train__title">Training</h2>
        <p className="bl-train__hint">
          When you’ve added classes and a few samples each, train the model and try it out.
        </p>
      </header>

      {!trainability.ok && !inProgress && (
        <p className="bl-train__hint bl-train__hint--blocked">{trainability.reason}</p>
      )}

      {trained && !inProgress && (
        <div className="bl-train__status" aria-live="polite">
          <span
            className={`bl-train__pip ${
              trained.converged === false ? 'bl-train__pip--learning' : 'bl-train__pip--ready'
            }`}
            aria-hidden="true"
          />
          {trained.converged === false ? (
            <>
              Trained {formatTimeAgo(trained.trainedAt)}. It is still learning. Try a few more
              pictures or train again.
            </>
          ) : (
            <>Trained {formatTimeAgo(trained.trainedAt)}.</>
          )}
          {trainedStaleness && (
            <span className="bl-train__stale">
              {' '}
              Samples have changed since you last trained. Train again to use them.
            </span>
          )}
        </div>
      )}

      {training.status === 'loading-mobilenet' && <Progress label="Getting the model ready…" />}

      {training.status === 'extracting' && (
        <Progress
          label={`Looking at your samples (${training.current} / ${training.total})`}
          value={training.total === 0 ? 0 : training.current / training.total}
        />
      )}

      {training.status === 'training' && (
        <div className="bl-train__progress" role="status" aria-live="polite">
          <p className="bl-train__progress-label">
            Teaching the model. Round {training.epoch} of {training.epochs}.
          </p>
          <div className="bl-train__bar" aria-hidden="true">
            <div
              className="bl-train__bar-fill"
              style={{ width: `${(training.epoch / training.epochs) * 100}%` }}
            />
          </div>
          <p className="bl-train__metrics">
            <span>loss {training.loss.toFixed(3)}</span>
            <span>accuracy {(training.accuracy * 100).toFixed(0)}%</span>
          </p>
        </div>
      )}

      {training.status === 'error' && (
        <p role="alert" className="bl-train__error">
          {training.message}
        </p>
      )}

      {inProgress && (
        <TrainingTip
          kind={kind}
          runKey={`${runId}:${training.status}`}
          phase={training.status === 'extracting' ? 'extracting' : 'training'}
        />
      )}

      {!inProgress && (
        <AdvancedTraining
          open={advancedOpen}
          onToggle={() => setAdvancedOpen((v) => !v)}
          values={hyperparams}
          onChange={setHyperparams}
          onReset={() => setHyperparams(DEFAULT_TRAIN_HYPERPARAMS)}
          trained={trained}
          kind={kind}
        />
      )}

      <div className="bl-train__actions">
        {inProgress ? (
          <button
            type="button"
            className="bl-train__button bl-train__button--cancel"
            onClick={onCancel}
          >
            Stop training
          </button>
        ) : (
          <>
            {/* Primary action — full width, top of the actions stack. */}
            <button
              type="button"
              className="bl-train__button"
              onClick={() => onTrain(isCustomHyperparams ? hyperparams : undefined)}
              disabled={!trainability.ok}
            >
              {trained ? 'Train again' : 'Train the model'}
            </button>
            {trained && (onTest || onQuiz) && (
              <div className="bl-train__try">
                {onTest && (
                  <button
                    type="button"
                    className="bl-train__button bl-train__button--test"
                    onClick={onTest}
                  >
                    Test the model
                  </button>
                )}
                {onQuiz && (
                  <button
                    type="button"
                    className="bl-train__button bl-train__button--quiz"
                    onClick={onQuiz}
                  >
                    Can you fool it?
                  </button>
                )}
              </div>
            )}
            {trained && (
              <p className="bl-train__hint">
                Open <strong>Test the model</strong> to try it out — that&rsquo;s where{' '}
                <em>download / make a website / add to Remix Gallery</em> live.
              </p>
            )}
          </>
        )}
      </div>
    </section>
  );
}

interface AdvancedTrainingProps {
  open: boolean;
  onToggle: () => void;
  values: Required<TrainHyperparams>;
  onChange: (next: Required<TrainHyperparams>) => void;
  onReset: () => void;
  /** When provided, the "Under the hood" sub-section renders a loss
   *  curve from the most recent training run. */
  trained: TrainedModel | null;
  /** Project kind drives the post-training tip pool (image / audio /
   *  pose vary in what's worth saying about the curve). */
  kind: ProjectKind;
}

/**
 * The TM-style "Advanced" section. Collapsed by default — kids never
 * have to think about epochs / batch size / learning rate, but the
 * parents and teachers who *do* know what those mean can crack it open
 * and tune. Inputs are clamped at submit time by the trainer's
 * `clampHyperparams` so a kid typing 9999 epochs can't brick the tab.
 */
function AdvancedTraining({
  open,
  onToggle,
  values,
  onChange,
  onReset,
  trained,
  kind,
}: AdvancedTrainingProps): JSX.Element {
  return (
    <div className={`bl-train__advanced${open ? ' bl-train__advanced--open' : ''}`}>
      <button
        type="button"
        className="bl-train__advanced-toggle"
        aria-expanded={open}
        aria-controls="bl-train-advanced-body"
        onClick={onToggle}
      >
        <span className="bl-train__advanced-caret" aria-hidden="true">
          {open ? '▾' : '▸'}
        </span>
        Advanced
      </button>
      {open && (
        <div className="bl-train__advanced-body" id="bl-train-advanced-body">
          <label className="bl-train__advanced-row">
            <span className="bl-train__advanced-name">
              Epochs
              <HelpTooltip label="What are epochs?">
                How many times the model looks at all your samples while it’s learning. More epochs
                means more practice, but too many can make it memorize instead of learning.
              </HelpTooltip>
            </span>
            <input
              type="number"
              min={TRAIN_HYPERPARAM_BOUNDS.epochs.min}
              max={TRAIN_HYPERPARAM_BOUNDS.epochs.max}
              step={1}
              value={values.epochs}
              onChange={(e) =>
                onChange({ ...values, epochs: Number.parseInt(e.target.value, 10) || 0 })
              }
              aria-label="Epochs"
            />
          </label>
          <label className="bl-train__advanced-row">
            <span className="bl-train__advanced-name">
              Batch size
              <HelpTooltip label="What is batch size?">
                How many samples the model studies at once before adjusting. Smaller batches learn
                from each example more carefully. Bigger batches are faster but a little less
                precise.
              </HelpTooltip>
            </span>
            <input
              type="number"
              min={TRAIN_HYPERPARAM_BOUNDS.batchSize.min}
              max={TRAIN_HYPERPARAM_BOUNDS.batchSize.max}
              step={1}
              value={values.batchSize}
              onChange={(e) =>
                onChange({ ...values, batchSize: Number.parseInt(e.target.value, 10) || 0 })
              }
              aria-label="Batch size"
            />
          </label>
          <label className="bl-train__advanced-row">
            <span className="bl-train__advanced-name">
              Learning rate
              <HelpTooltip label="What is learning rate?">
                How big a step the model takes when it learns something new. Too big and it bounces
                around. Too small and it learns very slowly. The default is a good starting point.
              </HelpTooltip>
            </span>
            <input
              type="number"
              min={TRAIN_HYPERPARAM_BOUNDS.learningRate.min}
              max={TRAIN_HYPERPARAM_BOUNDS.learningRate.max}
              step={0.0001}
              value={values.learningRate}
              onChange={(e) =>
                onChange({ ...values, learningRate: Number.parseFloat(e.target.value) || 0 })
              }
              aria-label="Learning rate"
            />
          </label>
          <button type="button" className="bl-train__advanced-reset" onClick={onReset}>
            Reset to defaults
          </button>
          <p className="bl-train__advanced-hint">
            Most projects work great with the defaults. Tweak these only if a model isn’t learning.
          </p>
          {trained && trained.lossHistory && trained.lossHistory.length > 0 && (
            <div className="bl-train__under-hood">
              <p className="bl-train__under-hood-title">
                Under the hood
                <HelpTooltip label="What is this?">
                  Each round of training, the model checks its mistakes and adjusts. This line shows
                  how those mistakes shrank. The lower it goes, the better the model got.
                </HelpTooltip>
              </p>
              <LossCurve
                lossHistory={trained.lossHistory}
                {...(trained.finalAccuracy !== undefined
                  ? { finalAccuracy: trained.finalAccuracy }
                  : {})}
              />
              {/* Rotating "how to read this" tip — fresh shuffle per
                  trained-model timestamp so every new run gets a new
                  starting line, but the same model keeps cycling. */}
              <TrainingTip kind={kind} runKey={trained.trainedAt} phase="post" intervalSec={8} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

interface ProgressProps {
  label: string;
  value?: number;
}

function Progress({ label, value }: ProgressProps): JSX.Element {
  return (
    <div className="bl-train__progress" role="status" aria-live="polite">
      <p className="bl-train__progress-label">{label}</p>
      <div className="bl-train__bar" aria-hidden="true">
        <div
          className="bl-train__bar-fill"
          style={{ width: value === undefined ? '100%' : `${Math.min(100, value * 100)}%` }}
        />
      </div>
    </div>
  );
}

function sampleCountsAreStale(trained: TrainedModel, classes: Class[], samples: Sample[]): boolean {
  const counts = new Map<string, number>();
  for (const sample of samples) {
    counts.set(sample.classId, (counts.get(sample.classId) ?? 0) + 1);
  }
  for (const cls of classes) {
    if ((trained.sampleCounts[cls.id] ?? 0) !== (counts.get(cls.id) ?? 0)) {
      return true;
    }
  }
  // Class added or removed since training.
  if (Object.keys(trained.sampleCounts).length !== classes.length) return true;
  return false;
}

function formatTimeAgo(timestamp: number): string {
  const seconds = Math.floor((Date.now() - timestamp) / 1000);
  if (seconds < 30) return 'just now';
  if (seconds < 90) return 'a minute ago';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} minutes ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}
